import React, { useState, useEffect } from 'react'
import Cookies from 'js-cookie'
import axios from 'axios'
import { Link, useNavigate } from 'react-router-dom'
import { toast, ToastContainer } from 'react-toastify'
import Select from 'react-select'
import 'react-toastify/dist/ReactToastify.css'
import Topbar from '../../layouts/Topbar'
import Sidebar from '../../layouts/Sidebar'
import { API_BASE_URL } from '../../../config/api'

const roleOptions = [
  { value: 'hr', label: 'HR' },
  { value: 'hod', label: 'HOD' },
  { value: 'cos', label: 'COS' },
]

const initialFormState = {
  email: '',
  role: null,
  code: '',
  first_name: '',
  last_name: '',
  department: null,
  secondary_department: null,
  national_id: '',
  position: '',
  occupation: ''
}

const COOCreateEmployee = () => {
  const navigate = useNavigate()
  const baseUrl = API_BASE_URL
  const [departments, setDepartments] = useState([])
  const [loading, setLoading] = useState(false)
  const [fetchingDepartments, setFetchingDepartments] = useState(true)
  const [form, setForm] = useState(initialFormState)

  useEffect(() => {
    const fetchDepartments = async () => {
      setFetchingDepartments(true)
      try {
        const token = Cookies.get('token')
        const response = await axios.get(`${baseUrl}/api/departments/`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        })
        const data = Array.isArray(response.data) ? response.data : []
        setDepartments(data)
      } catch (err) {
        toast.error(err.response?.data?.detail || err.response?.data?.message || 'Failed to fetch departments.')
      } finally {
        setFetchingDepartments(false)
      }
    }
    fetchDepartments()
  }, [baseUrl])

  const departmentOptions = departments.map(dep => ({
    value: dep.id,
    label: dep.course_center_name ? `${dep.name} (${dep.course_center_name})` : dep.name
  }))

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value })
  }

  const handleSelectChange = (selected, { name }) => {
    setForm({ ...form, [name]: selected })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    try {
      const token = Cookies.get('token')
      await axios.post(`${baseUrl}/api/employees/`, {
        email: form.email.trim(),
        role: form.role?.value,
        code: form.code.trim(),
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        department: form.department?.value,
        secondary_department: form.secondary_department?.value,
        national_id: form.national_id.trim(),
        position: form.position.trim(),
        occupation: form.occupation.trim()
      }, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })
      toast.success('Employee created successfully!')
      setForm(initialFormState)
      setTimeout(() => navigate('/coo/employees'), 1200)
    } catch (err) {
      const errorMessage = err.response?.data?.code?.[0] || err.response?.data?.message || err.response?.data?.detail || 'Failed to create employee.'
      toast.error(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  const isFormValid = Boolean(
    form.email.trim() &&
    form.role &&
    form.code.trim() &&
    form.first_name.trim() &&
    form.last_name.trim() &&
    form.department &&
    form.national_id.trim() &&
    form.position.trim() &&
    form.occupation.trim()
  )

  return (
    <>
      <ToastContainer position="top-right" autoClose={3500} hideProgressBar={false} />
      <div data-sidebar-skin="dark" data-header-skin="light" data-navbar-brand-skin="dark" data-sidebar-state="expand">
        <Topbar />
        <Sidebar />

        <div className="page-wrapper">
          <div className="content pb-0">
            <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-4">
              <div>
                <h4 className="fw-bold mb-1">Create Employee</h4>
                <p className="text-muted mb-0 fs-13">Create a new employee profile and assign role & department</p>
              </div>
              <Link to="/coo/employees" className="btn btn-light">
                <i className="ti ti-arrow-left me-1" />
                Back to Employees
              </Link>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="card shadow-sm mb-4">
                <div className="card-header">
                  <h5 className="fw-bold mb-0">Employee Information</h5>
                </div>
                <div className="card-body">
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label htmlFor="email" className="form-label">
                        Email Address <span className="text-danger">*</span>
                      </label>
                      <input
                        id="email"
                        type="email"
                        name="email"
                        className="form-control"
                        placeholder="e.g. john.doe@company.com"
                        value={form.email}
                        onChange={handleChange}
                        required
                        disabled={loading}
                      />
                    </div>

                    <div className="col-md-6">
                      <label htmlFor="role" className="form-label">
                        Role <span className="text-danger">*</span>
                      </label>
                      <Select
                        id="role"
                        name="role"
                        value={form.role}
                        onChange={handleSelectChange}
                        options={roleOptions}
                        placeholder="Select role"
                        classNamePrefix="react-select"
                        isDisabled={loading}
                        styles={{
                          control: (base) => ({ ...base, minHeight: '38px' }),
                          menuPortal: (base) => ({ ...base, zIndex: 9999 })
                        }}
                        menuPortalTarget={document.body}
                      />
                    </div>

                    <div className="col-md-4">
                      <label htmlFor="first_name" className="form-label">
                        First Name <span className="text-danger">*</span>
                      </label>
                      <input
                        id="first_name"
                        type="text"
                        name="first_name"
                        className="form-control"
                        placeholder="Enter first name"
                        value={form.first_name}
                        onChange={handleChange}
                        required
                        disabled={loading}
                      />
                    </div>

                    <div className="col-md-4">
                      <label htmlFor="last_name" className="form-label">
                        Last Name <span className="text-danger">*</span>
                      </label>
                      <input
                        id="last_name"
                        type="text"
                        name="last_name"
                        className="form-control"
                        placeholder="Enter last name"
                        value={form.last_name}
                        onChange={handleChange}
                        required
                        disabled={loading}
                      />
                    </div>

                    <div className="col-md-4">
                      <label htmlFor="code" className="form-label">
                        Employee Code <span className="text-danger">*</span>
                      </label>
                      <input
                        id="code"
                        type="text"
                        name="code"
                        className="form-control"
                        placeholder="e.g. EMP-001"
                        value={form.code}
                        onChange={handleChange}
                        required
                        disabled={loading}
                      />
                    </div>

                    <div className={form.role?.value === 'hod' ? 'col-md-6' : 'col-md-6'}>
                      <label htmlFor="department" className="form-label">
                        Primary Department <span className="text-danger">*</span>
                      </label>
                      <Select
                        id="department"
                        name="department"
                        value={form.department}
                        onChange={handleSelectChange}
                        options={departmentOptions}
                        placeholder={fetchingDepartments ? 'Loading departments...' : 'Select primary department'}
                        classNamePrefix="react-select"
                        isDisabled={loading || fetchingDepartments}
                        styles={{
                          control: (base) => ({ ...base, minHeight: '38px' }),
                          menuPortal: (base) => ({ ...base, zIndex: 9999 })
                        }}
                        menuPortalTarget={document.body}
                      />
                    </div>

                    {form.role?.value === 'hod' && (
                      <div className="col-md-6">
                        <label htmlFor="secondary_department" className="form-label">
                          Secondary Department <span className="text-muted fs-12">(Optional)</span>
                        </label>
                        <Select
                          id="secondary_department"
                          name="secondary_department"
                          value={form.secondary_department}
                          onChange={handleSelectChange}
                          options={departmentOptions}
                          placeholder={fetchingDepartments ? 'Loading departments...' : 'Select secondary department'}
                          classNamePrefix="react-select"
                          isDisabled={loading || fetchingDepartments}
                          styles={{
                            control: (base) => ({ ...base, minHeight: '38px' }),
                            menuPortal: (base) => ({ ...base, zIndex: 9999 })
                          }}
                          menuPortalTarget={document.body}
                        />
                      </div>
                    )}

                    <div className="col-md-4">
                      <label htmlFor="national_id" className="form-label">
                        National ID <span className="text-danger">*</span>
                      </label>
                      <input
                        id="national_id"
                        type="text"
                        name="national_id"
                        className="form-control"
                        placeholder="Enter national ID number"
                        value={form.national_id}
                        onChange={handleChange}
                        required
                        disabled={loading}
                      />
                    </div>

                    <div className="col-md-4">
                      <label htmlFor="position" className="form-label">
                        Position <span className="text-danger">*</span>
                      </label>
                      <input
                        id="position"
                        type="text"
                        name="position"
                        className="form-control"
                        placeholder="e.g. Senior Officer"
                        value={form.position}
                        onChange={handleChange}
                        required
                        disabled={loading}
                      />
                    </div>

                    <div className="col-md-4">
                      <label htmlFor="occupation" className="form-label">
                        Occupation <span className="text-danger">*</span>
                      </label>
                      <input
                        id="occupation"
                        type="text"
                        name="occupation"
                        className="form-control"
                        placeholder="e.g. Accountant"
                        value={form.occupation}
                        onChange={handleChange}
                        required
                        disabled={loading}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="d-flex align-items-center justify-content-end gap-2 mb-4">
                <Link to="/coo/employees" className="btn btn-light" onClick={(e) => loading && e.preventDefault()}>
                  Cancel
                </Link>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={loading || !isFormValid}
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                      Creating Employee...
                    </>
                  ) : (
                    <>
                      <i className="ti ti-device-floppy me-1" />
                      Create Employee
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>

          <div className="footer d-flex align-items-center justify-content-between flex-column flex-sm-row row-gap-2 border-top py-2 px-3">
            <p className="text-dark mb-0">
              2026 © <span className="link-primary">Casoe Technologies</span>, All Rights Reserved
            </p>
          </div>
        </div>
      </div>
    </>
  )
}

export default COOCreateEmployee
